// TeacherInformationSection.js
import { useState, useEffect } from "react"
import moment from "moment"
import ReactPaginate from "react-paginate"

import { Grid, InputAdornment } from "@mui/material"
import Icon from "@mui/material/Icon"
import SearchIcon from "@mui/icons-material/Search"
import { selectAuth } from "shared/redux/slices/authSlice"

import { createTeacherInfoMockApiResponse } from "./mockData"

import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import DataTable from "components/DataTable"
import MDButton from "components/MDButton"
import { Loading } from "shared/components/ui/Loading"
import MDInput from "components/MDInput"
import { actionButtonStyle } from "components/CenterDashboardSection/style"
import TeacherManagementTimeSheetViewDetail from "components/TeacherManagementTimeSheetViewDetail/TeacherManagementTimeSheetViewDetail"

import { useLazyGetTimeSheetLXOQuery } from "shared/redux/endpoints/lxo/teacherManagement"
import { useSelector } from "react-redux"

const TABLE_HEADERS = [
  { Header: "No", accessor: "id" },
  { Header: "Teacher name", accessor: "teacherName" },
  { Header: "Contract Type", accessor: "contractType" },
  { Header: "Teaching hour", accessor: "teachingHour" },
  { Header: "PT/Demo hour", accessor: "ptDemoHour" },
  { Header: "Total", accessor: "total" },
  { Header: "No. of PH", accessor: "noOfPH" },
  { Header: "No. of AL", accessor: "noOfAL" },
  { Header: "Adjusted hour (+PH/AL)", accessor: "adjustedHour" },
  { Header: "Actual required hour", accessor: "actualRequiredHour" },
  { Header: "Efficiency", accessor: "efficiency" },
  { Header: "Action", accessor: "action" },
]

export default function TeacherManagementTimeSheetSection() {
  // State management matching CenterDashboardSection

  const auth = useSelector(selectAuth)

  const centerId = auth?.centerId

  const [state, setState] = useState({
    page: 0,
    size: 10,
    // sort: "id,desc",
    // start: moment().format("DD-MM-YYYY"),
    // end: moment().format("DD-MM-YYYY"),
    keyword: "",
    viewDetailId: null,
    centerId,
  })

  const buildColumnsFromDays = (days) => [
    { Header: "Class/Event", accessor: "name" },
    ...days.map((d) => ({ Header: d, accessor: d })), // accessor dùng đúng string ngày
    { Header: "Total by month", accessor: "totalByMonth" },
  ]

  const [isLoading, setIsLoading] = useState(true)
  const [dataTable, setDataTable] = useState({
    columns: TABLE_HEADERS,
    rows: [],
  })
  const [pagination, setPagination] = useState({
    totalPage: 0,
    currentPage: 0,
  })

  const [activeTimeSheet, setActiveTimeSheet] = useState(null)

  const [trigger, { data }] = useLazyGetTimeSheetLXOQuery()
  const [debouncedKeyword, setDebouncedKeyword] = useState(state.keyword)

  const handlerDataTable = (data) => {
    setDataTable({
      columns: TABLE_HEADERS,
      rows: data.map((item, index) => ({
        ...item,
        id: <MDTypography>{index + 1}</MDTypography>,
        teacherName: <MDTypography>{item.teacherName}</MDTypography>,
        contractType: (
          <MDTypography>
            {item.contractType === "FULL_TIME" ? "Full Time" : "Part Time"}
          </MDTypography>
        ),
        teachingHour: <MDTypography>{item.teachingHours}</MDTypography>,
        ptDemoHour: <MDTypography>{item.ptDemoHours}</MDTypography>,
        total: <MDTypography>{item.totalHours}</MDTypography>,
        noOfPH: <MDTypography>{item.publicHolidays}</MDTypography>,
        noOfAL: <MDTypography>{item.annualLeaves}</MDTypography>,
        adjustedHour: <MDTypography>{item.adjustedHours}</MDTypography>,
        actualRequiredHour: (
          <MDTypography>{item.actualRequiredHours}</MDTypography>
        ),
        efficiency: (
          <MDTypography
            sx={{
              color:
                item.efficiency > 100 ? "green !important" : "red !important",
            }}
          >
            {item.efficiency}%
          </MDTypography>
        ),
        action: (
          <MDButton
            sx={(theme) => ({ ...actionButtonStyle(theme) })}
            onClick={() => setState({ ...state, viewDetailId: item.teacherId })}
          >
            <Icon sx={{}}>visibility</Icon>
            View
          </MDButton>
        ),
      })),
    })
  }

  const handlderAPIList = (params) => {
    setIsLoading(true)
    trigger(params).then((res) => {
      handlerDataTable(res?.data?.data?.data)
      setPagination({
        ...pagination,
        totalPage: res?.data?.data?.totalPage,
        currentPage: res?.data?.data?.currentPage,
      })
      setIsLoading(false)
    })
  }

  const buildListParams = (overrides = {}) => ({
    page: state.page,
    size: state.size,
    search: debouncedKeyword,
    centerId,
    ...overrides,
  })

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setState((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "keyword" ? { page: 0 } : {}),
    }))
  }
  const handleChangePage = (page) => {
    setState((prev) => ({ ...prev, page }))
  }

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(state.keyword)
    }, 400)
    return () => clearTimeout(timer)
  }, [state.keyword])

  // Fetch data when filters change
  useEffect(() => {
    if (!centerId) return
    handlderAPIList(buildListParams())
  }, [centerId, state.page, state.size, debouncedKeyword])

  const tableViewDetailProps = {
    viewDetailId: state.viewDetailId,
    onGoBack: () => setState({ ...state, viewDetailId: null }),
  }

  return (
    <Grid
      item
      xs={12}
      lg={8}
      xxl={7.5}
      className={`teacher-management__section`}
    >
      {state.viewDetailId ? (
        <TeacherManagementTimeSheetViewDetail {...tableViewDetailProps} />
      ) : (
        <MDBox className="teacher-management__table">
          <MDBox className="teacher-management__table-header">
            <MDTypography variant="h3" className="info-icon__title">
              Timesheet
            </MDTypography>
          </MDBox>

          <MDBox className="teacher-management__table-filters">
            <MDInput
              className="input input-search"
              placeholder="Search here..."
              name="keyword"
              value={state.keyword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="icon-search" />
                  </InputAdornment>
                ),
              }}
              onChange={handleInputChange}
            />
          </MDBox>
          <MDBox
            className="teacher-management__table-wrapper"
            sx={{ position: "relative" }}
          >
            {isLoading && (
              <MDBox
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.6)",
                }}
              >
                <Loading style={{ height: 120 }} />
              </MDBox>
            )}
            <DataTable
              table={dataTable}
              entriesPerPage={{
                isShown: true,
                defaultValue: state.size,
                entries: [5, 10, 25],
                pagination: true,
              }}
            />
            <MDBox className="table-footer">
              <MDBox className="table-footer-showing">
                Showing {dataTable?.rows?.length} of {dataTable?.rows?.length}{" "}
                entries
              </MDBox>
              {pagination.totalPage >= 0 && (
                <ReactPaginate
                  className="pagination"
                  nextLabel={<Icon>chevron_right</Icon>}
                  previousLabel={<Icon>chevron_left</Icon>}
                  forcePage={Number.isFinite(state.page) ? state.page : 0}
                  onPageChange={(event) => {
                    handleChangePage(event.selected)
                  }}
                  pageCount={pagination.totalPage}
                />
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      )}
    </Grid>
  )
}
